import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadZone = ({ onUpload, onClose, selectedFolder }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files?.filter(file => {
      // Basic file validation
      const maxSize = 50 * 1024 * 1024; // 50MB
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain'
      ];
      
      return file?.size <= maxSize && allowedTypes?.includes(file?.type);
    });

    if (validFiles?.length > 0) {
      processFiles(validFiles);
    }
  };

  const processFiles = (files) => {
    const newQueue = files?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file?.name,
      size: file?.size,
      type: file?.type,
      status: 'pending',
      progress: 0,
      suggestedFolder: getSuggestedFolder(file),
      tags: getSuggestedTags(file)
    }));

    setUploadQueue(prev => [...prev, ...newQueue]);
    
    // Start uploading files
    newQueue?.forEach(fileItem => {
      uploadFile(fileItem);
    });
  };

  const getSuggestedFolder = (file) => {
    const fileName = file?.name?.toLowerCase();
    const fileType = file?.type?.toLowerCase();
    
    if (fileName?.includes('invoice') || fileName?.includes('bill')) {
      return 'invoices';
    } else if (fileName?.includes('receipt')) {
      return 'receipts';
    } else if (fileName?.includes('contract') || fileName?.includes('agreement')) {
      return 'contracts';
    } else if (fileName?.includes('tax') || fileName?.includes('vat')) {
      return 'tax-documents';
    } else if (fileType?.includes('image')) {
      return 'receipts';
    } else if (fileType?.includes('pdf')) {
      return 'invoices';
    }
    
    return selectedFolder || 'general';
  };

  const getSuggestedTags = (file) => {
    const fileName = file?.name?.toLowerCase();
    const tags = [];
    
    if (fileName?.includes('2024')) tags?.push('2024');
    if (fileName?.includes('invoice')) tags?.push('invoice');
    if (fileName?.includes('receipt')) tags?.push('receipt');
    if (fileName?.includes('tax')) tags?.push('tax');
    if (fileName?.includes('contract')) tags?.push('contract');
    
    return tags;
  };

  const uploadFile = async (fileItem) => {
    try {
      setUploadProgress(prev => ({
        ...prev,
        [fileItem?.id]: { status: 'uploading', progress: 0 }
      }));

      // TODO: Implement actual file upload to storage service
      // Remove this simulation code when implementing real upload
      
      setUploadProgress(prev => ({
        ...prev,
        [fileItem?.id]: { status: 'completed', progress: 100 }
      }));

      // Create document object for callback
      const document = {
        id: Date.now() + Math.random(),
        name: fileItem?.name,
        type: fileItem?.file?.type?.split('/')?.[1] || 'unknown',
        size: fileItem?.size,
        uploadDate: new Date()?.toISOString(),
        folderId: fileItem?.suggestedFolder,
        tags: fileItem?.tags,
        thumbnail: fileItem?.file?.type?.startsWith('image/') ? URL.createObjectURL(fileItem?.file) : null
      };

      onUpload(document);

      // Remove from queue after completion
      setTimeout(() => {
        setUploadQueue(prev => prev?.filter(item => item?.id !== fileItem?.id));
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress?.[fileItem?.id];
          return newProgress;
        });
      }, 2000);

    } catch (error) {
      setUploadProgress(prev => ({
        ...prev,
        [fileItem?.id]: { status: 'error', progress: 0 }
      }));
    }
  };

  const removeFromQueue = (fileId) => {
    setUploadQueue(prev => prev?.filter(item => item?.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress?.[fileId];
      return newProgress;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-lg shadow-floating w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Upload Documents</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Upload Zone */}
        <div className="p-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
              isDragOver
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Drop files here or click to browse
            </h3>
            <p className="text-muted-foreground mb-4">
              Support for PDF, images, Word docs, Excel files up to 50MB
            </p>
            
            <Button
              onClick={() => fileInputRef?.current?.click()}
              iconName="FolderOpen"
            >
              Choose Files
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt"
            />
          </div>
        </div>

        {/* Upload Queue */}
        {uploadQueue?.length > 0 && (
          <div className="border-t border-border">
            <div className="p-4">
              <h3 className="text-lg font-medium text-foreground mb-4">Upload Progress</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {uploadQueue?.map((fileItem) => {
                  let progress = uploadProgress?.[fileItem?.id];
                  return (
                    <div key={fileItem?.id} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                      <Icon name="File" size={20} className="text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{fileItem?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(fileItem?.size)} • Folder: {fileItem?.suggestedFolder}
                        </p>
                        
                        {progress && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                              <span className="capitalize">{progress?.status}</span>
                              <span>{progress?.progress}%</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full transition-all duration-300 ${
                                  progress?.status === 'completed' ? 'bg-success' :
                                  progress?.status === 'error' ? 'bg-error' : 'bg-primary'
                                }`}
                                style={{ width: `${progress?.progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {progress?.status === 'completed' && (
                          <Icon name="CheckCircle" size={16} className="text-success" />
                        )}
                        {progress?.status === 'error' && (
                          <Icon name="XCircle" size={16} className="text-error" />
                        )}
                        
                        <button
                          onClick={() => removeFromQueue(fileItem?.id)}
                          className="p-1 hover:bg-muted rounded"
                        >
                          <Icon name="X" size={14} className="text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadZone;